import React, { useEffect } from "react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import Hero from "@/components/ledger/Hero";
import CertificationsStrip from "@/components/ledger/CertificationsStrip";
import BrandStatement from "@/components/ledger/BrandStatement";
import ProductShowcase from "@/components/ledger/ProductShowcase";
import GlobalPresence from "@/components/ledger/GlobalPresence";
import Provenance from "@/components/ledger/Provenance";
import InquirySection from "@/components/ledger/InquirySection";
import Footer from "@/components/ledger/Footer";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import SectionDivider from "@/components/ledger/SectionDivider";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";

/**
 * Public landing - Archival Trade Ledger × Editorial Magazine.
 * All section components live under components/ledger/. The whole page is
 * wrapped in `.ledger` so the design system (colors, type, rules)
 * applies here only - admin panel + auth screen retain their own look.
 *
 * Section divider variants between sections deliberately differ to give
 * a sense of pacing - diamond for editorial gaps, seal for the most
 * emphatic transition (after the brand statement), pure rule before
 * the practical sections.
 */
export default function VeloraLanding() {
  useSeo({
    title: "Vilora Impex | Indian Rice, Spices & Mango Exporter",
    description:
      "Vilora Impex exports Indian rice, spices, pulses, dry fruits and mangoes from Gujarat with FSSAI/APEDA documentation and bulk shipment support.",
    path: "/",
    image: "https://www.viloraimpex.com/og.jpg",
    imageAlt: "Vilora Impex Indian rice, spices and mango exporter",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://www.viloraimpex.com/#webpage",
      "url": "https://www.viloraimpex.com/",
      "name": "Vilora Impex | Indian Rice, Spices & Mango Exporter",
      "isPartOf": { "@id": "https://www.viloraimpex.com/#website" },
      "about": { "@id": "https://www.viloraimpex.com/#organization" },
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": "https://www.viloraimpex.com/og.jpg",
        "width": 1200,
        "height": 630,
      },
      "inLanguage": "en",
      "description":
        "Vilora Impex is an Indian trading house from Gujarat exporting rice, spices, pulses, dry fruits and mangoes with documentation and bulk shipment support.",
    },
  });

  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;

    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <main data-testid="velora-landing" className="ledger relative min-h-screen">
      <ScrollProgress />
      <Navbar />
      <Hero />
      <CertificationsStrip />
      <SectionDivider variant="seal" label="Four Divisions" />
      <ProductShowcase />
      <SectionDivider variant="diamond" label="The House" />
      <BrandStatement />
      <SectionDivider variant="diamond" label="Routes & Timetable" />
      <GlobalPresence />
      <SectionDivider variant="diamond" label="Provenance" />
      <Provenance />
      <SectionDivider variant="seal" label="Open a File" />
      <InquirySection />
      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
